import type { SearchResponse } from '~/contracts/SearchRepositoryContract'
import type { ProductRequestResponse } from '~/stores/product'

export interface ProductCache {
  key: string
  pages: number[]
  data: ProductRequestResponse
}

interface fetchProductsFromCachePayload {
  key: string
  page: number
  fetchFn: () => Promise<ProductRequestResponse> | Promise<SearchResponse>
}

export function useProductCache() {
  const pagesLoaded = useState<number[]>('pagesLoaded', () => [])
  const cache = useState<Map<string, ProductCache>>('productCategoryCache', () => new Map())

  function cacheProducts(key: string, page: number, data: ProductRequestResponse): ProductCache {
    let cacheEntry = cache.value.get(key)

    if (!cacheEntry) {
      cacheEntry = {
        key,
        pages: [page],
        data,
      }

      cache.value.set(key, cacheEntry)

      return cacheEntry
    }

    const pageIsAlreadyCached = cacheEntry && cacheEntry.pages.includes(page)

    if (pageIsAlreadyCached) {
      return cacheEntry
    }

    if (cacheEntry) {
      const minPage = Math.min(...cacheEntry.pages) || 1
      const prepend = page < minPage

      if (prepend) {
        cacheEntry.pages.unshift(page)
        cacheEntry.data.products.unshift(...data.products)
      }
      else {
        cacheEntry.pages.push(page)
        cacheEntry.data.products.push(...data.products)
      }
    }

    cache.value.set(key, cacheEntry)

    return cacheEntry
  }

  function getCachedProducts(key: string): ProductCache | undefined {
    return cache.value.get(key)
  }

  async function fetchProductsFromCache(
    payload: fetchProductsFromCachePayload,
  ): Promise<ProductRequestResponse> {
    const { key, page, fetchFn } = payload

    const cached = getCachedProducts(key)
    const isPageCached = cached && cached.pages.includes(page)

    pagesLoaded.value = cached ? cached.pages : []

    if (isPageCached) {
      return Promise.resolve({
        id: cached.data.id,
        products: cached.data.products,
        pages: cached.data.pages,
        total: cached.data.total,
      })
    }

    const newProducts = await fetchFn()

    const { data } = cacheProducts(key, page, newProducts)

    pagesLoaded.value.push(page)

    return Promise.resolve(data)
  }

  return {
    fetchProductsFromCache,

    pagesLoaded,
  }
}
